import { createSlice } from '@reduxjs/toolkit';

// State interface
interface TiptapToolbarState {
  dialogs: {
    color: {
      open: boolean;
      value: string;
      anchorEl: HTMLElement | null;
    };
    backgroundColor: {
      open: boolean;
      value: string;
      anchorEl: HTMLElement | null;
    };
    link: {
      open: boolean;
      value: string;
    };
    image: {
      open: boolean;
      value: string;
      uploading: boolean;
      uploadError: string | null;
      uploadFile: File | null;
    };
    katex: {
      open: boolean;
      value: string;
    };
  };
  imageModal: {
    isOpen: boolean;
    imageSrc: string | null;
  };
}

// Initial state
const initialState: TiptapToolbarState = {
  dialogs: {
    color: {
      open: false,
      value: '',
      anchorEl: null
    },
    backgroundColor: {
      open: false,
      value: '',
      anchorEl: null
    },
    link: {
      open: false,
      value: ''
    },
    image: {
      open: false,
      value: '',
      uploading: false,
      uploadError: null,
      uploadFile: null
    },
    katex: {
      open: false,
      value: ''
    }
  },
  imageModal: {
    isOpen: false,
    imageSrc: null
  }
};

// Tiptap Toolbar slice
const slice = createSlice({
  name: 'tiptapToolbar',
  initialState,
  reducers: {
    // Color dialog
    openColorDialog: (state, action) => {
      state.dialogs.color.open = true;
      if (typeof action.payload === 'object' && action.payload !== null) {
        state.dialogs.color.value = action.payload.value || '';
        state.dialogs.color.anchorEl = action.payload.anchorEl || null;
      } else {
        state.dialogs.color.value = action.payload || '';
        state.dialogs.color.anchorEl = null;
      }
    },
    closeColorDialog: (state) => {
      state.dialogs.color.open = false;
      state.dialogs.color.value = '';
      state.dialogs.color.anchorEl = null;
    },
    setColorValue: (state, action) => {
      state.dialogs.color.value = action.payload;
    },

    // Background color dialog
    openBackgroundColorDialog: (state, action) => {
      state.dialogs.backgroundColor.open = true;
      if (typeof action.payload === 'object' && action.payload !== null) {
        state.dialogs.backgroundColor.value = action.payload.value || '';
        state.dialogs.backgroundColor.anchorEl = action.payload.anchorEl || null;
      } else {
        state.dialogs.backgroundColor.value = action.payload || '';
        state.dialogs.backgroundColor.anchorEl = null;
      }
    },
    closeBackgroundColorDialog: (state) => {
      state.dialogs.backgroundColor.open = false;
      state.dialogs.backgroundColor.value = '';
      state.dialogs.backgroundColor.anchorEl = null;
    },
    setBackgroundColorValue: (state, action) => {
      state.dialogs.backgroundColor.value = action.payload;
    },

    // Link dialog
    openLinkDialog: (state, action) => {
      state.dialogs.link.open = true;
      state.dialogs.link.value = action.payload || '';
    },
    closeLinkDialog: (state) => {
      state.dialogs.link.open = false;
    },
    setLinkValue: (state, action) => {
      state.dialogs.link.value = action.payload;
    },

    // Image dialog
    openImageDialog: (state, action) => {
      state.dialogs.image.open = true;
      state.dialogs.image.value = action.payload || '';
      state.dialogs.image.uploading = false;
      state.dialogs.image.uploadError = null;
      state.dialogs.image.uploadFile = null;
    },
    closeImageDialog: (state) => {
      state.dialogs.image.open = false;
      state.dialogs.image.value = '';
      state.dialogs.image.uploading = false;
      state.dialogs.image.uploadError = null;
      state.dialogs.image.uploadFile = null;
    },
    setImageValue: (state, action) => {
      state.dialogs.image.value = action.payload;
    },
    setImageUploadFile: (state, action) => {
      state.dialogs.image.uploadFile = action.payload;
    },
    setImageUploading: (state, action) => {
      state.dialogs.image.uploading = action.payload;
    },
    setImageUploadError: (state, action) => {
      state.dialogs.image.uploadError = action.payload;
    },

    // KaTeX dialog
    openKatexDialog: (state, action) => {
      state.dialogs.katex.open = true;
      state.dialogs.katex.value = action.payload || '';
    },
    closeKatexDialog: (state) => {
      state.dialogs.katex.open = false;
    },
    setKatexValue: (state, action) => {
      state.dialogs.katex.value = action.payload;
    },

    // Image modal
    openImageModal: (state, action) => {
      state.imageModal.isOpen = true;
      state.imageModal.imageSrc = action.payload || null;
    },
    closeImageModal: (state) => {
      state.imageModal.isOpen = false;
      state.imageModal.imageSrc = null;
    }
  }
});

export default slice.reducer;

// Export actions
export const {
  openColorDialog,
  closeColorDialog,
  setColorValue,
  openBackgroundColorDialog,
  closeBackgroundColorDialog,
  setBackgroundColorValue,
  openLinkDialog,
  closeLinkDialog,
  setLinkValue,
  openImageDialog,
  closeImageDialog,
  setImageValue,
  setImageUploadFile,
  setImageUploading,
  setImageUploadError,
  openKatexDialog,
  closeKatexDialog,
  setKatexValue,
  openImageModal,
  closeImageModal
} = slice.actions;
